import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { companyAPI, subscriptionsAPI } from "@/lib/api";
import { Loading } from "@/components/ui/loader";
import { AlertCircle, Check, Minus, RefreshCw } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { usePermission } from "@/hooks/usePermission";
import ChangePlanDialog from "./ChangePlanDialog";
import { SettingsSurface } from "@/features/settings/components/SettingsSurface";

interface SubscriptionPlan {
	id: number;
	name: string;
	slug: string;
	durationMonths: number | null;
	features: Record<string, unknown> | null;
	isActive: boolean;
}

interface FeatureDef {
	key: string;
	labelKey: string;
	type: string;
	default: number | boolean;
}

interface CurrentSubscription {
	plan: { id: number; name: string; slug: string; durationMonths: number | null } | null;
	subscriptionStartedAt: string | null;
	subscriptionEndsAt: string | null;
	status: "active" | "expired" | "none";
	effectiveFeatures: Record<string, number | boolean>;
}

function FeatureValue({ value, type }: { value: unknown; type: string }) {
	const { t } = useTranslation();
	if (value === null || value === undefined)
		return <span className="text-xs text-muted-foreground">{t("subscription.notSpecified")}</span>;
	if (type !== "boolean")
		return (
			<span className="font-semibold tabular-nums" dir="auto">
				{String(value)}
			</span>
		);
	return (
		<span
			className={`inline-flex items-center gap-1.5 text-xs ${value ? "text-foreground" : "text-muted-foreground"}`}
		>
			{value ? (
				<Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
			) : (
				<Minus className="h-4 w-4 shrink-0" aria-hidden="true" />
			)}
			{t(value ? "subscription.included" : "subscription.notIncluded")}
		</span>
	);
}

/** Read-only subscription overview. Effective limits continue to come from the server. */
export default function SubscriptionSection() {
	const { t } = useTranslation();
	const canChangePlan = usePermission("company.update");
	const [current, setCurrent] = useState<CurrentSubscription | null>(null);
	const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
	const [features, setFeatures] = useState<FeatureDef[]>([]);
	const [loading, setLoading] = useState(true);
	const [attempt, setAttempt] = useState(0);
	const [errors, setErrors] = useState<{ label: string; forbidden: boolean }[]>([]);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		Promise.allSettled([
			companyAPI.getSubscription(),
			subscriptionsAPI.getPlans(),
			subscriptionsAPI.getFeatures(),
		]).then(([sub, plansList, featuresList]) => {
			if (cancelled) return;
			setCurrent(sub.status === "fulfilled" ? sub.value : null);
			setPlans(plansList.status === "fulfilled" && Array.isArray(plansList.value) ? plansList.value : []);
			setFeatures(
				featuresList.status === "fulfilled" && Array.isArray(featuresList.value) ? featuresList.value : [],
			);
			const labels = [
				"subscription.currentSubscription",
				"subscription.planComparison",
				"subscription.effectiveFeatures",
			];
			setErrors(
				[sub, plansList, featuresList].flatMap((result, index) =>
					result.status === "rejected"
						? [{ label: labels[index], forbidden: result.reason?.response?.status === 403 }]
						: [],
				),
			);
			setLoading(false);
		});
		return () => {
			cancelled = true;
		};
	}, [attempt]);

	if (loading)
		return (
			<SettingsSurface>
				<Loading />
			</SettingsSurface>
		);

	const featureError = errors.some(e => e.label === "subscription.effectiveFeatures");
	const plansError = errors.some(e => e.label === "subscription.planComparison");
	// Preserve the assigned plan and dates even if it is absent from the catalogue.
	const comparisonPlans: SubscriptionPlan[] = [...plans];
	if (current?.plan && !plans.some(plan => plan.id === current.plan!.id)) {
		comparisonPlans.push({
			...current.plan,
			features: current.effectiveFeatures,
			isActive: current.status === "active",
		});
	}

	return (
		<div className="space-y-6" data-testid="subscription-content">
			{errors.length > 0 && (
				<SettingsSurface>
					<div className="flex flex-wrap items-center justify-between gap-4">
						<div role="alert" className="flex items-start gap-3">
							<AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
							<div className="space-y-1 text-sm">
								{errors.map(error => (
									<p key={error.label}>
										{t(error.label)}:{" "}
										{t(error.forbidden ? "subscription.accessDenied" : "subscription.loadError")}
									</p>
								))}
							</div>
						</div>
						<Button variant="outline" onClick={() => setAttempt(value => value + 1)}>
							<RefreshCw className="me-2 h-4 w-4" aria-hidden="true" />
							{t("common.retry")}
						</Button>
					</div>
				</SettingsSurface>
			)}

			<section aria-labelledby="subscription-comparison-heading">
				<div className="mb-4">
					<h2 id="subscription-comparison-heading" className="text-base font-semibold">
						{t("subscription.planComparison")}
					</h2>
					<p className="mt-1 text-sm text-muted-foreground">{t("subscription.planComparisonDescription")}</p>
					{current && !current.plan && (
						<p className="mt-3 text-sm text-muted-foreground">
							{t("subscription.noPlan")}. {t("subscription.defaultAccess")}
						</p>
					)}
				</div>
				{comparisonPlans.length === 0 ? (
					!plansError && (
						<SettingsSurface>
							<p className="text-sm text-muted-foreground">{t("subscription.noPlans")}</p>
						</SettingsSurface>
					)
				) : (
					<div className="subscription-plans">
						{comparisonPlans.map(plan => {
							const assigned = current?.plan?.id === plan.id;
							const active = assigned && current?.status === "active";
							return (
								<SettingsSurface
									key={plan.id}
									className={
										assigned ? "subscription-plan subscription-plan-current" : "subscription-plan"
									}
								>
									<div className="subscription-plan-heading">
										<div className="mb-3 flex min-h-7 flex-wrap gap-2">
											{assigned ? (
												<>
													<Badge variant="secondary">{t("subscription.currentPlan")}</Badge>
													{current?.status !== "none" && (
														<Badge variant={active ? "success" : "danger"}>
															{t(`subscription.status.${current?.status}`)}
														</Badge>
													)}
												</>
											) : !plan.isActive ? (
												<Badge>{t("subscription.unavailable")}</Badge>
											) : null}
										</div>
										<h3 className="break-words text-lg font-semibold" dir="auto">
											{plan.name}
										</h3>
										{plan.durationMonths != null && (
											<p className="mt-1 text-xs text-muted-foreground">
												{t("subscription.duration", { count: plan.durationMonths })}
											</p>
										)}
									</div>
									{assigned && !active && (
										<p className="mb-3 text-sm text-muted-foreground">
											{t("subscription.defaultAccess")}
										</p>
									)}
									{!featureError &&
										(features.length > 0 ? (
											<dl>
												{features.map(f => (
													<div key={f.key} className="subscription-feature">
														<dt>{t(f.labelKey)}</dt>
														<dd>
															<FeatureValue
																type={f.type}
																value={
																	assigned
																		? (current?.effectiveFeatures?.[f.key] ??
																			f.default)
																		: plan.features?.[f.key]
																}
															/>
														</dd>
													</div>
												))}
											</dl>
										) : (
											<p className="text-sm text-muted-foreground">
												{t("subscription.noFeatures")}
											</p>
										))}
									{assigned && (
										<dl
											className="mt-4 grid grid-cols-2 gap-4 border-t border-border/60 pt-4 text-xs"
											data-testid="subscription-dates"
										>
											{[
												["subscription.startedAt", current?.subscriptionStartedAt],
												["subscription.endsAt", current?.subscriptionEndsAt],
											].map(([label, value]) => (
												<div key={label}>
													<dt className="mb-2 text-muted-foreground">{t(label as string)}</dt>
													<dd className="font-medium tabular-nums">
														{value ? (
															<time dateTime={value} dir="ltr">
																{formatDate(value)}
															</time>
														) : (
															t("subscription.notSpecified")
														)}
													</dd>
												</div>
											))}
										</dl>
									)}
								</SettingsSurface>
							);
						})}
					</div>
				)}
			</section>
			{canChangePlan && (
				<div className="flex justify-end">
					<ChangePlanDialog />
				</div>
			)}
		</div>
	);
}
